import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Bot, User as UserIcon } from "lucide-react";

// Very light markdown rendering (bold, headings, code, tables) — good enough for AI replies
function renderMd(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let tableBuf = [];
  const flushTable = (key) => {
    if (tableBuf.length === 0) return;
    const rows = tableBuf.map((r) => r.split("|").map((c) => c.trim()).filter((c, i, arr) => !(i === 0 && c === "") && !(i === arr.length - 1 && c === "")));
    const header = rows[0];
    const body = rows.slice(2);
    elements.push(
      <div key={key} className="my-2 overflow-x-auto border border-zinc-200 rounded-sm">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50"><tr>{header.map((h, i) => <th key={i} className="px-2 py-1.5 text-left font-semibold text-zinc-700">{h}</th>)}</tr></thead>
          <tbody>{body.map((r, ri) => <tr key={ri} className="border-t border-zinc-100">{r.map((c, ci) => <td key={ci} className="px-2 py-1.5 text-zinc-700 font-mono">{c}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
    tableBuf = [];
  };
  lines.forEach((ln, i) => {
    if (ln.trim().startsWith("|")) { tableBuf.push(ln); return; }
    flushTable(`t-${i}`);
    if (/^#{1,3}\s/.test(ln)) {
      const level = ln.match(/^#+/)[0].length;
      const txt = ln.replace(/^#+\s*/, "");
      elements.push(React.createElement(`h${level}`, { key: i, className: "font-semibold mt-3 mb-1 text-zinc-900" }, txt));
    } else if (ln.trim() === "") {
      elements.push(<div key={i} className="h-2" />);
    } else if (/^[-*]\s/.test(ln)) {
      elements.push(<li key={i} className="ml-4 list-disc text-sm text-zinc-700">{ln.replace(/^[-*]\s/, "")}</li>);
    } else {
      const html = ln.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/`([^`]+)`/g, '<code class="font-mono text-xs bg-zinc-100 px-1">$1</code>');
      elements.push(<p key={i} className="text-sm text-zinc-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />);
    }
  });
  flushTable("t-final");
  return elements;
}

export default function ChatSheet({ open, onOpenChange }) {
  const [sessionId] = useState(() => `sess-${Date.now()}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (messages.length === 0 && open) {
      setMessages([{
        role: "assistant",
        content: "Bonjour ! Je suis Py, votre assistant IA.\n\nDécrivez-moi votre déploiement : nombre d'utilisateurs, nombre de sites, type de client, cas d'usage (Pare-feu, SD-WAN, SASE, Zero Trust, SOC), niveau de sécurité et durée de licence. Je vous proposerai un BOM et BOQ conformes avec le coût total.",
      }]);
    }
  }, [open, messages.length]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/chat", { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (err) {
      toast.error("Assistant IA indisponible");
      setMessages((m) => [...m, { role: "assistant", content: "_Désolé, le service IA est injoignable. Veuillez réessayer._" }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[480px] p-0 flex flex-col border-l-4 border-l-[#D42B1E]" data-testid="ai-chat-sheet">
        <SheetHeader className="px-5 py-4 border-b border-zinc-200">
          <SheetTitle className="flex items-center gap-2 text-zinc-900">
            <Bot className="h-4 w-4 text-[#D42B1E]" />
            Assistant IA Pythagore
          </SheetTitle>
          <SheetDescription className="text-xs">Py · Assistant IA Pythagore</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-zinc-50/30" data-testid="ai-chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && (
                <div className="h-7 w-7 rounded-sm bg-[#D42B1E] text-white flex items-center justify-center shrink-0">
                  <Bot className="h-3.5 w-3.5" />
                </div>
              )}
              <div className={`max-w-[85%] ${m.role === "user" ? "bg-zinc-900 text-white rounded-sm px-3 py-2" : "bg-white border border-zinc-200 rounded-sm px-3 py-2"}`}>
                {m.role === "user" ? (
                  <p className="text-sm">{m.content}</p>
                ) : (
                  <div>{renderMd(m.content)}</div>
                )}
              </div>
              {m.role === "user" && (
                <div className="h-7 w-7 rounded-sm bg-zinc-900 text-white flex items-center justify-center shrink-0">
                  <UserIcon className="h-3.5 w-3.5" />
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex gap-3">
              <div className="h-7 w-7 rounded-sm bg-[#D42B1E] text-white flex items-center justify-center"><Bot className="h-3.5 w-3.5" /></div>
              <div className="bg-white border border-zinc-200 rounded-sm px-3 py-2">
                <div className="flex gap-1">
                  <span className="h-1.5 w-1.5 bg-zinc-400 rounded-full animate-pulse" />
                  <span className="h-1.5 w-1.5 bg-zinc-400 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <span className="h-1.5 w-1.5 bg-zinc-400 rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-zinc-200 p-3 bg-white">
          <div className="flex gap-2">
            <Input
              placeholder="Décrivez le déploiement Fortinet..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              className="rounded-sm"
              data-testid="ai-chat-input"
            />
            <Button onClick={send} disabled={sending || !input.trim()} className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm" data-testid="ai-chat-send-button">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
